import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Text } from '@abyss/web/ui/Text';
import React, { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { phoneOnly } from '../../../../common/ConstantsStyles';
import { getFeatureFlag } from '../../../../common/Utils';
import { handleLinkAndModalTrack } from '../../../../common/Utils/adobeTrackUtils';
import { useGetTokenOrUrlParams } from '../../../../hooks/useInitializeStore';
import { useLagoon } from '../../../../hooks/useLagoon';
import { usePrintPSX } from '../../../../hooks/usePSXPrint/usePrintPSX';
import { CompareProvider } from '../../../../models/Provider';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { sleep } from '../../../../utils';
import {
  FilterProviderResultsPrintView,
  FilterProviderResultsPrintViewProps,
} from '../MapView/Print/FilterProviderResultsPrintView';
import { PrintView } from '../MapView/Print/PrintView';
import {
  hideAskAIWidget,
  hideChat,
  hideFeedbackContainer,
  hideToasts,
  hideYourFeedbackForm,
  hideYourOpinionMattersPopup,
} from '../MapView/ShareDrawer/utils';
import { ShareCompareButtonStyled } from './ShareCompareButton';

type Props = {
  text: string;
  view: String;
  setOpenBoxContents: (a: boolean) => void;
  setOpenShare: (a: boolean) => void;
  setOpenCompare: (a: boolean) => void;
  setSelectedCheckbox: (a: { checked: {} }) => void;
  setSelectedItems: (a: CompareProvider[]) => void;
  isClicked: boolean;
  setIsClicked: React.Dispatch<React.SetStateAction<boolean>>;
  enableMapViewEnhancements: boolean;
  pageSize?: number;
  printPage?: number;
  results?: any[];
  filterProviderResultsPrintViewProps?: FilterProviderResultsPrintViewProps;
  setCompareShareToggle?: (a: boolean) => void;
};

export const DownloadResultsButton = ({
  text,
  setOpenBoxContents,
  setOpenShare,
  setCompareShareToggle,
  setSelectedCheckbox,
  setSelectedItems,
  setOpenCompare,
  isClicked,
  setIsClicked,
  enableMapViewEnhancements,
  view,
  results,
  printPage,
  filterProviderResultsPrintViewProps,
}: Props) => {
  const { printPSXPage } = usePrintPSX();
  const textColor = '$info1';
  const mobileScreen = useMediaQuery(phoneOnly);
  const urlParams = useGetTokenOrUrlParams();
  const featureFlags = useLagoon('feature-flags')();
  const showDownloadResultsButton = getFeatureFlag(
    featureFlags,
    'SHARE_PDF_ALL_RESULTS'
  );
  const enableEnhancedPdfPrintBhFacility: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ENHANCED_PDF_PRINT_BH_FACILITY
  );
  const enablePrintDefectFix: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.SHARE_PRINT_PDF_REFRESH_FIX
  );

  const handleAdobeButtonClickTrack = (buttonName) => {
    const linkName = `${buttonName} button`;
    const location = `body:${chipValue} view all results`;
    const modalName: string = `${buttonName} print PDF view`;
    handleLinkAndModalTrack(linkName, location, modalName);
  };

  const { chipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      chipValue: state.chipValue,
    }))
  );

  const isFacility = urlParams?.sectionType === 'Facility';
  const isPractitioner = urlParams?.sectionType === 'Provider';
  const isBehavioralHealth =
    chipValue === Constants.CHIPS_CATEGORIES.BEHAVIORAL_HEALTH;

  const shouldEnableEnhancedPdfPrintBhFacility =
    isBehavioralHealth && isFacility && enableEnhancedPdfPrintBhFacility;

  const shouldEnableEnhancedPdfPrintBhProvider =
    isBehavioralHealth && isPractitioner;

  const includeAdditionalFieldsForPrint =
    shouldEnableEnhancedPdfPrintBhFacility ||
    shouldEnableEnhancedPdfPrintBhProvider;

  const handleButton = async () => {
    setOpenShare(false);
    setOpenCompare(false);
    setCompareShareToggle?.(false);
    if (!enablePrintDefectFix) {
      await sleep(1000);
    }
    setIsClicked(true);
    setOpenBoxContents(false);
    setSelectedCheckbox({ checked: {} });
    setSelectedItems([]);
    hideFeedbackContainer(true);
    hideChat(true);
    hideToasts(true);
    hideAskAIWidget(true);
    hideYourFeedbackForm(true);
    hideYourOpinionMattersPopup(true);
    if (results && filterProviderResultsPrintViewProps == null) {
      // otherwise, <FilterProviderResultsPrintView> will printPage when ready.

      setTimeout(() => {
        // Wait for Brandmark and other assets to load before printing
        printPSXPage();
      }, 100);
    }
    hideChat(false);
    hideFeedbackContainer(false);
    hideToasts(false);
    hideAskAIWidget(false);
    hideYourFeedbackForm(false);
    hideYourOpinionMattersPopup(false);
    handleAdobeButtonClickTrack('download results');
  };

  useEffect(() => {
    const handleAfterPrint = () => {
      setIsClicked(false);
    };
    window.onafterprint = handleAfterPrint;

    return () => {
      window.onafterprint = null;
    };
  }, [isClicked]);

  return (
    showDownloadResultsButton && (
      <ShareCompareButtonStyled
        aria-pressed={!!isClicked}
        css={
          mobileScreen &&
          !enableMapViewEnhancements && {
            '&.abyss-button-root': {
              justifyContent: 'start !important',
              border: 'solid 2px $primary1',
              '&:active, &:focus, &:hover': {
                backgroundColor: '$tint3',
              },
            },
            width: '188px',
            justifyContent: 'start',
          }
        }
        data-auto-testid="filter-buttons-download-results"
        data-testid="filter-buttons-download-results"
        id="mybutton"
        onClick={() => {
          handleButton();
        }}
        size={mobileScreen && !enableMapViewEnhancements ? '$sm' : '$md'}
        variant={
          mobileScreen && !enableMapViewEnhancements ? 'outline' : 'ghost'
        }
        view={view}
      >
        <Flex alignContent="center" alignItems="center" justify="center">
          <IconMaterial color="$info1" icon="download" />
          <Text
            color={
              mobileScreen && !enableMapViewEnhancements
                ? '$primary1'
                : textColor
            }
            css={{ paddingLeft: '5px' }}
            data-auto-testid="filter-buttons-download-results"
            data-testid="filter-buttons-download-results"
            fontWeight={mobileScreen && !enableMapViewEnhancements && 600}
          >
            {text}
          </Text>
          {filterProviderResultsPrintViewProps == null && (
            <PrintView selectedProviderList={results} />
          )}
          {filterProviderResultsPrintViewProps != null && (
            <FilterProviderResultsPrintView
              coverageType={filterProviderResultsPrintViewProps.coverageType}
              filters={filterProviderResultsPrintViewProps.filters}
              includeAdditionalFieldsForPrint={includeAdditionalFieldsForPrint}
              initialPage={filterProviderResultsPrintViewProps.initialPage}
              isClicked={isClicked}
              printPage={printPage || 1}
              setIsClicked={setIsClicked}
            />
          )}
        </Flex>
      </ShareCompareButtonStyled>
    )
  );
};
